<template>
  <a-modal
    title="报告详情"
    :visible="visible"
    :width="1120"
    :footer="null"
    :body-style="{ padding: 0 }"
    wrap-class-name="report-detail-modal"
    centered
    @cancel="cancel"
  >
    <a-spin :spinning="loading">
      <div v-if="data" class="detail-shell">
        <div class="report-overview">
          <div class="overview-identity">
            <div class="name-avatar">{{ nameInitial }}</div>
            <div class="overview-copy">
              <div class="overview-title-row">
                <h2>{{ data.name || '未命名儿童' }}</h2>
                <span class="edition-tag">{{ editionLabel }}</span>
                <span class="input-mode-tag" :class="{ detailed: hasDetailScores }">
                  {{ hasDetailScores ? '逐题录入' : '手工录入' }}
                </span>
              </div>
              <div class="overview-meta">
                <span><a-icon type="file-text" />记分编号 {{ formatValue(data.scoring_num) }}</span>
                <span><a-icon type="calendar" />测试日期 {{ formatValue(data.test_date) }}</span>
                <span><a-icon type="user" />主试 {{ formatValue(data.examiner_name) }}</span>
              </div>
            </div>
          </div>
          <div class="overview-age">
            <span>实际年龄</span>
            <strong>{{ formatValue(data.real_age) }}</strong>
          </div>
        </div>

        <div class="detail-content">
          <section class="detail-section">
            <div class="section-heading">
              <span class="section-icon"><a-icon type="profile" /></span>
              <div>
                <h3>基本信息</h3>
                <p>儿童身份与本次测评信息</p>
              </div>
            </div>
            <baseInfo v-if="base" :data="base" />
          </section>

          <section class="detail-section">
            <div class="section-heading">
              <span class="section-icon score"><a-icon type="bar-chart" /></span>
              <div>
                <h3>原始分数</h3>
                <p>{{ editionLabel }}分测验结果</p>
              </div>
            </div>
            <div class="score-groups">
              <div v-for="group in scoreGroups" :key="group.title" class="score-group">
                <div class="score-group-title">
                  <span>{{ group.title }}</span>
                  <small>{{ group.items.length }} 项</small>
                </div>
                <div class="score-grid">
                  <div v-for="score in group.items" :key="score.key" class="score-item">
                    <span>{{ score.label }}</span>
                    <strong>{{ formatValue(data[score.key]) }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section class="detail-section">
            <div class="section-heading">
              <span class="section-icon setting"><a-icon type="setting" /></span>
              <div>
                <h3>统计参数</h3>
                <p>报告计算与比较设置</p>
              </div>
            </div>
            <div class="parameter-grid">
              <div v-for="parameter in parameters" :key="parameter.label" class="parameter-item">
                <span>{{ parameter.label }}</span>
                <strong>{{ parameter.value }}</strong>
              </div>
            </div>
          </section>

          <section v-if="hasDetailScores" class="detail-section detail-score-section">
            <div class="section-heading">
              <span class="section-icon detail"><a-icon type="appstore" /></span>
              <div>
                <h3>详细分数</h3>
                <p>按分测验查看各题得分</p>
              </div>
            </div>
            <wisc-score-detail :value="detailScores" :edition="String((data && data.edition) || '4')" readonly />
          </section>
        </div>
      </div>
    </a-spin>
  </a-modal>
</template>

<script>
import { Detail } from "../../commonApi";
import WiscScoreDetail from "../wisc-score-detail";

const COMPLETE_SCORE_GROUPS = [
  {
    title: '主要原始分',
    items: [
      { label: '积木', key: 'score_bd' },
      { label: '类同', key: 'score_si' },
      { label: '背数', key: 'score_ds' },
      { label: '译码', key: 'score_cd' },
      { label: '词汇', key: 'score_vc' },
      { label: '矩阵推理', key: 'score_mr' },
      { label: '理解', key: 'score_co' },
      { label: '符号检索', key: 'score_ss' },
    ],
  },
  {
    title: '补充原始分',
    items: [
      { label: '图画概念', key: 'score_pc' },
      { label: '字母-数字', key: 'score_ln' },
      { label: '填图', key: 'score_pcm' },
      { label: '划消', key: 'score_ca' },
      { label: '常识', key: 'score_in' },
      { label: '算术', key: 'score_ar' },
    ],
  },
  {
    title: '过程分数',
    items: [
      { label: '积木无速度加分', key: 'score_bdn' },
      { label: '顺序背数', key: 'score_dsf' },
      { label: '顺序背数最大长度', key: 'score_dsf_max' },
      { label: '倒序背数', key: 'score_dsb' },
      { label: '倒序背数最大长度', key: 'score_dsb_max' },
      { label: '随机划消', key: 'score_car' },
      { label: '有序划消', key: 'score_cas' },
    ],
  },
];

const SIMPLE_SCORE_GROUPS = [
  {
    title: '简版原始分',
    items: [
      { label: '积木', key: 'score_bd' },
      { label: '类同', key: 'score_si' },
      { label: '背数', key: 'score_ds' },
      { label: '译码', key: 'score_cd' },
      { label: '矩阵推理', key: 'score_mr' },
      { label: '理解', key: 'score_co' },
    ],
  },
];

export default {
  components: {WiscScoreDetail},
  props: {
    item: [Object],
    detailVisible: [Boolean],
  },
  data() {
    return {
      visible: false,
      loading: false,
      id: "",
      data: null,
      base: null,
    };
  },
  computed: {
    nameInitial() {
      return this.data && this.data.name ? this.data.name.slice(0, 1) : '童';
    },
    editionLabel() {
      return this.data && String(this.data.edition) === '5' ? '简版' : '完整版';
    },
    scoreGroups() {
      return this.data && String(this.data.edition) === '5' ? SIMPLE_SCORE_GROUPS : COMPLETE_SCORE_GROUPS;
    },
    parameters() {
      return [
        {
          label: '置信区间',
          value: this.formatValue(this.data && this.data.confidence_probability, '%'),
        },
        {
          label: '显著性水平',
          value: this.formatValue(this.data && this.data.significance_level),
        },
        {
          label: '比较基础',
          value: this.comparisonBasisLabel,
        },
        {
          label: '计算方法',
          value: this.computingMethodLabel,
        },
      ];
    },
    comparisonBasisLabel() {
      if (!this.data) return '-';
      if (String(this.data.comparison_basis) === '1') return '全样本';
      if (String(this.data.comparison_basis) === '2') return '能力水平';
      return '-';
    },
    computingMethodLabel() {
      if (!this.data) return '-';
      if (String(this.data.computing_method) === '1') return '替代分测验';
      if (String(this.data.computing_method) === '2') return '比例估算法';
      return '-';
    },
    detailScores() {
      const detailScores = this.data && this.data.detail_scores;
      if (detailScores && typeof detailScores === 'object') return detailScores;
      if (typeof detailScores === 'string' && detailScores) {
        try {
          return JSON.parse(detailScores);
        } catch (e) {
          return {};
        }
      }
      return {};
    },
    hasDetailScores() {
      return this.data && String(this.data.detail_score_enabled) === '1' && Object.keys(this.detailScores).length > 0;
    },
  },
  watch: {
    item: {
      handler(e) {
        if (!e) return;
        this.data = e;
        this.id = e.id;
      },
      immediate: true,
      deep: true,
    },
    detailVisible: {
      handler(e) {
        this.visible = e;
        if (this.visible && this.id) this.getDetail();
      },
      immediate: true,
    },
  },
  methods: {
    formatValue(value, suffix = '') {
      if (value === null || value === undefined || value === '') return '-';
      return `${value}${suffix}`;
    },
    async getDetail() {
      this.loading = true;
      this.base = null;
      try {
        const res = await Detail({ id: this.id });
        if (res && res.code == 0) {
          this.data = res.wiscAssessment;
          this.base = {
            archives_num: res.wiscAssessment.archives_num,
            scoring_num: res.wiscAssessment.scoring_num,
            name: res.wiscAssessment.name,
            examiner_name: res.wiscAssessment.examiner_name,
            gender: res.wiscAssessment.gender,
            real_age: res.wiscAssessment.real_age,
            is_minorities: res.wiscAssessment.is_minorities,
            hand_habit: res.wiscAssessment.hand_habit,
            exam_address: res.wiscAssessment.exam_address,
            born_date: res.wiscAssessment.born_date,
            remark: res.wiscAssessment.remark,
            test_date: res.wiscAssessment.test_date,
            parent_culture: res.wiscAssessment.parent_culture,
            grade: res.wiscAssessment.grade,
          };
        }
      } finally {
        this.loading = false;
      }
    },
    cancel() {
      this.visible = false;
      this.$emit("detailCancel");
    },
  },
};
</script>

<style scoped>
.detail-shell {
  color: #27313a;
  background: #f5f7f8;
}

.report-overview {
  min-height: 112px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 30px;
  color: #fff;
  background: #3c263a;
}

.overview-identity,
.overview-title-row,
.overview-meta {
  display: flex;
  align-items: center;
}

.overview-identity {
  min-width: 0;
  gap: 16px;
}

.name-avatar {
  width: 54px;
  height: 54px;
  flex: 0 0 54px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 50%;
  background: #fff;
  color: #832c75;
  font-size: 22px;
  font-weight: 700;
  line-height: 52px;
  text-align: center;
}

.overview-copy {
  min-width: 0;
}

.overview-title-row {
  flex-wrap: wrap;
  gap: 8px;
}

.overview-title-row h2 {
  margin: 0 4px 0 0;
  color: #fff;
  font-size: 22px;
  font-weight: 600;
  line-height: 30px;
}

.edition-tag,
.input-mode-tag {
  padding: 3px 9px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 20px;
}

.edition-tag {
  background: rgba(255, 255, 255, 0.16);
}

.input-mode-tag {
  color: #d7e8e5;
  background: rgba(67, 137, 127, 0.3);
}

.input-mode-tag.detailed {
  color: #fff1d7;
  background: rgba(185, 128, 37, 0.34);
}

.overview-meta {
  flex-wrap: wrap;
  gap: 8px 20px;
  margin-top: 10px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 12px;
}

.overview-meta span {
  white-space: nowrap;
}

.overview-meta .anticon {
  margin-right: 6px;
}

.overview-age {
  min-width: 150px;
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  margin-left: 20px;
}

.overview-age span {
  color: rgba(255, 255, 255, 0.65);
  font-size: 12px;
}

.overview-age strong {
  margin-top: 6px;
  color: #fff;
  font-size: 17px;
  font-weight: 600;
}

.detail-content {
  max-height: calc(100vh - 210px);
  overflow-y: auto;
  padding: 0 30px 30px;
}

.detail-section {
  padding: 28px 0;
  border-bottom: 1px solid #dde2e5;
}

.detail-section:last-child {
  border-bottom: 0;
}

.section-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}

.section-icon {
  width: 38px;
  height: 38px;
  display: grid;
  flex: 0 0 38px;
  place-items: center;
  border-radius: 6px;
  color: #832c75;
  background: #eee5ed;
  font-size: 17px;
}

.section-icon.score {
  color: #28685f;
  background: #e1eeeb;
}

.section-icon.setting {
  color: #8a611b;
  background: #f3ead9;
}

.section-icon.detail {
  color: #3c5f83;
  background: #e3ebf3;
}

.section-heading h3 {
  margin: 0;
  color: #27313a;
  font-size: 17px;
  font-weight: 600;
  line-height: 24px;
}

.section-heading p {
  margin: 2px 0 0;
  color: #8a939a;
  font-size: 12px;
}

.score-groups {
  display: grid;
  gap: 18px;
}

.score-group {
  min-width: 0;
}

.score-group-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 9px;
  color: #5d666d;
  font-size: 13px;
  font-weight: 600;
}

.score-group-title small {
  color: #a1a8ad;
  font-weight: 400;
}

.score-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.score-item {
  min-height: 66px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 15px;
  border: 1px solid #e1e5e7;
  border-radius: 6px;
  background: #fff;
}

.score-item span {
  min-width: 0;
  color: #6f777d;
  font-size: 13px;
}

.score-item strong {
  margin-left: 12px;
  color: #27313a;
  font-size: 21px;
  font-weight: 600;
}

.parameter-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border: 1px solid #e1e5e7;
  border-radius: 6px;
  background: #fff;
}

.parameter-item {
  min-height: 78px;
  display: flex;
  justify-content: center;
  padding: 16px 18px;
  border-right: 1px solid #e8ebed;
  flex-direction: column;
}

.parameter-item:last-child {
  border-right: 0;
}

.parameter-item span {
  color: #8a939a;
  font-size: 12px;
}

.parameter-item strong {
  margin-top: 7px;
  color: #27313a;
  font-size: 15px;
  font-weight: 600;
}

.detail-score-section ::v-deep .detail-score-panel {
  margin: 0;
}

@media (max-width: 900px) {
  .report-overview {
    align-items: flex-start;
  }

  .overview-age {
    min-width: 0;
  }

  .score-grid,
  .parameter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .parameter-item:nth-child(2) {
    border-right: 0;
  }

  .parameter-item:nth-child(-n + 2) {
    border-bottom: 1px solid #e8ebed;
  }
}

@media (max-width: 600px) {
  .report-overview {
    min-height: 0;
    padding: 18px;
    flex-direction: column;
  }

  .overview-identity {
    align-items: flex-start;
  }

  .name-avatar {
    width: 44px;
    height: 44px;
    flex-basis: 44px;
    font-size: 18px;
    line-height: 42px;
  }

  .overview-title-row h2 {
    font-size: 19px;
  }

  .overview-meta {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }

  .overview-age {
    align-items: flex-start;
    margin: 15px 0 0 60px;
  }

  .detail-content {
    max-height: calc(100vh - 190px);
    padding: 0 18px 20px;
  }

  .detail-section {
    padding: 22px 0;
  }

  .score-grid,
  .parameter-grid {
    grid-template-columns: 1fr;
  }

  .parameter-item {
    border-right: 0;
    border-bottom: 1px solid #e8ebed;
  }

  .parameter-item:last-child {
    border-bottom: 0;
  }
}
</style>

<style>
.report-detail-modal .ant-modal {
  max-width: calc(100vw - 24px);
}

.report-detail-modal .ant-modal-header {
  padding: 16px 22px;
  border-bottom-color: #e8ebed;
}

.report-detail-modal .ant-modal-title {
  color: #27313a;
  font-weight: 600;
}

.report-detail-modal .ant-modal-close-x {
  width: 54px;
  height: 54px;
  line-height: 54px;
}
</style>
